Returns the public details of one specific call for the authenticated tenant.

## What this endpoint returns

- complete operational classification for the call;
- public campaign and agent references;
- destination number metadata;
- key lifecycle timestamps;
- recording, variables, sanitized collected data, public segments, and termination origin.

<ApiCodeExamples exampleId="callDetail" />

<ApiResponseExamples exampleId="callDetail" />

## Main fields in the `200` response

- `id`: call ID.
- `canonical_url`: canonical Contact-Tel app link for the call.
- `campaign_id` and `campaign_name`: associated campaign when present.
- `campaign_contact_id` and `campaign_contact_display_name`: public reference to the related contact.
- `public_api_call_contact_id` and `public_api_call_contact_status`: parent public API contact job correlation.
- `client_reference`: opaque client reference from the parent job, or `null` when not applicable.
- `retry_policy`: immutable parent-job retry policy snapshot, or `null` for legacy/non-public calls.
- `attempts`: parent-job dial progress with `count`, `remaining`, and `next_attempt_at`.
- `public_api_call_contact_phone_id`, `public_api_call_contact_phone_status`, and `public_api_call_contact_phone_position`: phone-attempt correlation inside the public API contact job.
- `created_by_id`, `created_by_name`, `created_by_extension`: creator/originator user reference.
- `transfer_target_user_*`, `transfer_target_group_*`, `interaction_claimed_by_*`, `interaction_answered_by_*`: human transfer and participant references.
- `telephony_transfer_*`: human handoff timeline.
- `sip_trunk_id`, `sip_trunk_name`: compatible SIP trunk snapshot.
- `sip_trunk_pool_id`, `sip_trunk_pool_name`, `sip_trunk_pool_member_id`: outbound pool/member snapshot when applicable.
- `sip_trunk_outbound_route_id`, `sip_trunk_outbound_route_name`: outbound route used by the call.
- `routing_decision`: public outbound routing decision snapshot when available.
- `ai_billed_seconds`, `ai_gross_billed_seconds`, `ai_refunded_seconds`: net, gross and refunded AI consumption.
- `telephony_billed_seconds`, `telephony_gross_billed_seconds`, `telephony_refunded_seconds`: net, gross and refunded telephony consumption.
- `to_number_*`: public metadata resolved for the destination number.
- `from_number_real`: actual origin observed from the provider when available.
- `recording_download_url`: temporary call recording download URL when available.
- `variables`: resolved call/contact variables used during execution, excluding internal `sys__*` variables.
- `data_collected`: collected conversation data consolidated from public call segments. Each item only contains `value`, `rationale` and sanitized `__source`.
- `segments`: public call segments with transcript, summary and recording.
- `segments[].transcript_full`: sanitized structured transcript. Each item only contains `role`, `message`, `original_message` and `time_in_call_secs`.
- `ring_duration`: ringing duration in seconds.
- `ivr_audio_heard_seconds`, `ivr_audio_heard_to_end`: Reverse IVR playback evidence.
- `termination_origin`: public origin that caused the call to end.

## Option reference

- `direction`: `outbound` means the platform placed the call; `inbound` means the platform received it.
- `origin`: `campaign` is campaign-created; `manual` is user/operator-created; `public_api` is public-API-created.
- `call_type`: `ai_agent` is an AI-agent call; `reverse_ivr` is audio plus DTMF; `sip` is a SIP call.
- `status`: `pending`, `queued`, `dialing`, `analyzing`, `ringing`, `in_progress`, `finalizing`, `waiting_capacity`, `waiting_operating_hours`, `completed`, `no_interaction`, `failed`, `no_answer`, `no_answer_timeout`, `busy`, `cancelled`, `transfer_unanswered`, `congestion`, `voicemail`, `invalid_number`, `config_error`, `declined`, `unreachable`, `timeout`, `mismatch`, `api_timeout`, `terminated_manual`, `capacity_timeout`, `operating_window_timeout`.
- `to_number_phone_type`: `unknown`, `mobile`, `landline`, or `sip`.
- `termination_origin`: `unknown`, `pstn`, `agent`, `manual`, `system`, or `internal`.
- `segments[].segment_type`: `ai`, `telephony_internal_extension`, `telephony_outbound_internal_extension`, `telephony_external_extension`, `telephony_external_phone`, or `ivr`.

## When to use this endpoint

- fetch one call after receiving its `id` from the list endpoint;
- power operational drill-downs in external systems;
- investigate the status and outcome of a specific call without using the admin UI.
